import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireQuoteRecordCustomer, TireQuoteRecordList } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { NoddiCircularLoader, NoddiSheet } from 'noddi-ui-web';
import {
  formatCurrencyAmount,
  formatLicensePlate,
  getCarDisplayName,
  getTireBrandName,
  getTirePrice,
  NoddiDateFormats,
  noddiFormatDate
} from 'noddi-util';

import { ApiErrorMessageWithTrans } from '../../../../components/Error/ApiErrorMessageWithTrans';
import { getGenericItemsTotal } from '../utils/applyStrategy';
import { getProgressStep, getQuoteStatusValue, getStepMeta } from '../utils/statusMapping';
import { TireOfferProgressStrip } from './TireOfferProgressStrip';

type AcceptedDetailPanelProps = {
  offer: TireQuoteRecordList | null;
  onClose: () => void;
};

export const AcceptedDetailPanel = ({ offer, onClose }: AcceptedDetailPanelProps) => {
  const open = offer != null;
  return (
    <NoddiSheet
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t`Accepted offer`}
      description={offer?.slug}
      side='right'
      className='max-sm:w-full max-sm:max-w-full'
    >
      {offer && <DetailContent offer={offer} />}
    </NoddiSheet>
  );
};

const DetailContent = ({ offer: listOffer }: { offer: TireQuoteRecordList }) => {
  const {
    data: offer,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getCarTireQuoteBySlug,
    input: { carTireQuoteSlug: listOffer.slug }
  });

  if (isPending) {
    return <NoddiCircularLoader />;
  }
  if (error) {
    return <ApiErrorMessageWithTrans error={error} />;
  }

  const status = getQuoteStatusValue(offer);
  const step = getProgressStep(status);
  const meta = getStepMeta(step);

  const upcomingDate = offer.upcomingBooking?.deliveryWindowStartsAt;
  const eta = step === 3 ? t`Delivered` : upcomingDate ? formatEtaText(upcomingDate) : t`Awaiting schedule`;
  const paidDate = offer.paidAt ? noddiFormatDate(offer.paidAt, NoddiDateFormats.DATE) : null;
  const expectedDate = upcomingDate ? noddiFormatDate(upcomingDate, NoddiDateFormats.DATE) : null;
  const dateLine =
    step === 3 && paidDate ? t`Completed on ${paidDate}` : expectedDate ? t`Expected ${expectedDate}` : null;

  const selectedQuoteItem = offer.quoteItemSelected ?? offer.quoteItems.find((item) => item.isSelected);
  const headlineTire = selectedQuoteItem?.quoteItemTires[0];
  const tireSubtotal = selectedQuoteItem
    ? selectedQuoteItem.quoteItemTires.reduce(
        (sum, tire) => sum + getTirePrice(tire).amount * (tire.quantitySelected || tire.quantityDefault),
        0
      )
    : 0;
  const genericItemsTotal = getGenericItemsTotal(offer);
  const total = tireSubtotal + genericItemsTotal;
  const currency = headlineTire?.tireInventory.salesItem.price.priceGross.currency ?? 'NOK';
  const tireQuantity = selectedQuoteItem
    ? selectedQuoteItem.quoteItemTires.reduce((sum, tire) => sum + (tire.quantitySelected || tire.quantityDefault), 0)
    : offer.quantitySelected;

  return (
    <div className='flex flex-col gap-5'>
      <Section className='relative overflow-hidden'>
        <div className='pointer-events-none absolute inset-0 bg-card-light-background opacity-60' aria-hidden />
        <div className='relative flex min-w-0 flex-col gap-3'>
          <span
            className={cn(
              'inline-flex w-fit max-w-full min-w-0 flex-wrap items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium break-words whitespace-normal',
              meta.badge
            )}
          >
            <span className={cn('size-1.5 rounded-full', meta.dot)} />
            {meta.label}
          </span>
          <div className='text-xl font-semibold tracking-tight break-words text-foreground'>{eta}</div>
          {dateLine && <div className='text-xs break-words text-muted-foreground'>{dateLine}</div>}
          <TireOfferProgressStrip current={step} />
        </div>
      </Section>

      <Section title={t`Vehicle`}>
        <div className='flex flex-col gap-2'>
          <div className='text-sm font-semibold'>{getCarDisplayName({ car: offer.car, showLicensePlate: false })}</div>
          {offer.car.licensePlateNumber && (
            <span className='inline-flex w-fit rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground'>
              {formatLicensePlate(offer.car.licensePlateNumber)}
            </span>
          )}
          <KvDivider />
          <Kv label={t`Customer`} value={offer.userGroup?.name ?? '—'} />
        </div>
      </Section>

      <Section title={t`Tire ordered`}>
        <div className='flex items-start gap-4'>
          {headlineTire?.tireInventory.imageTire?.url ? (
            <img
              src={headlineTire.tireInventory.imageTire.url}
              alt=''
              className='size-16 shrink-0 rounded-sm object-contain'
            />
          ) : (
            <div className='size-16 shrink-0 rounded-sm bg-muted' />
          )}
          <div className='flex min-w-0 flex-col gap-1'>
            <div className='text-sm font-semibold'>
              {headlineTire && getTireBrandName(headlineTire.tireInventory.tire)}
            </div>
            <div className='text-xs text-muted-foreground'>{headlineTire?.tireInventory.tire.model ?? ''}</div>
          </div>
        </div>
        <KvDivider />
        <Kv label={t`Quantity`} value={t`${tireQuantity} pcs`} />
        {headlineTire && (
          <Kv label={t`Per tire`} value={formatCurrencyAmount({ money: getTirePrice(headlineTire), decimals: 0 })} />
        )}
        <Kv
          label={t`Tires subtotal`}
          value={formatCurrencyAmount({ money: { amount: tireSubtotal, currency }, decimals: 0 })}
        />
        {offer.genericItems.map((item) => (
          <Kv
            key={item.id}
            label={item.name}
            value={formatCurrencyAmount({ money: item.price.priceGross, decimals: 0 })}
          />
        ))}
        <Kv label={t`Total`} value={formatCurrencyAmount({ money: { amount: total, currency }, decimals: 0 })} bold />
      </Section>

      <Section title={t`Delivery`}>
        <Kv
          label={t`Method`}
          value={offer.hasActiveTireHotelContractForWheelSet ? t`Mounted at facility` : t`Delivered to address`}
        />
        {offer.address && (
          <Kv label={t`Location`} value={[offer.address.streetName, offer.address.city].filter(Boolean).join(', ')} />
        )}
      </Section>

      <Section title={t`Activity`}>
        <ActivityTimeline offer={offer} />
      </Section>
    </div>
  );
};

const Section = ({ title, children, className }: { title?: string; children: React.ReactNode; className?: string }) => (
  <section
    className={cn('flex shrink-0 flex-col gap-3 rounded-xl border border-border bg-card p-5 text-sm', className)}
  >
    {title && <div className='text-[11px] font-semibold tracking-wider text-muted-foreground uppercase'>{title}</div>}
    {children}
  </section>
);

const Kv = ({ label, value, bold }: { label: string; value: string; bold?: boolean }) => (
  <div className='flex items-start justify-between gap-3 py-1.5'>
    <span className='min-w-0 flex-1 text-xs text-muted-foreground'>{label}</span>
    <span
      className={cn(
        'max-w-[60%] min-w-0 text-right text-sm break-words text-foreground tabular-nums',
        bold && 'font-bold'
      )}
    >
      {value}
    </span>
  </div>
);

const KvDivider = () => <div className='border-t border-border' />;

const ActivityTimeline = ({ offer }: { offer: TireQuoteRecordCustomer }) => {
  const events: { title: string; detail?: string; at?: string }[] = [];
  if (offer.status?.createdAt) {
    events.push({
      title: getActivityTitleForStatus(getQuoteStatusValue(offer)) ?? t`Status update`,
      at: offer.status.createdAt
    });
  }
  if (offer.upcomingBooking?.deliveryWindowStartsAt) {
    events.push({
      title: t`Mounting booked`,
      detail: t`Tire mounting scheduled`,
      at: offer.upcomingBooking.deliveryWindowStartsAt
    });
  }
  if (offer.paidAt) {
    events.push({ title: t`Payment received`, at: offer.paidAt });
  }
  if (offer.selectedAt) {
    events.push({ title: t`Offer accepted`, at: offer.selectedAt });
  }

  events.sort((a, b) => (a.at && b.at ? new Date(b.at).getTime() - new Date(a.at).getTime() : 0));

  if (events.length === 0) {
    return (
      <div className='text-sm text-muted-foreground'>
        <Trans>No activity yet.</Trans>
      </div>
    );
  }

  return (
    <div className='flex flex-col'>
      {events.map((event, index) => (
        <div key={index} className='relative flex gap-3 pb-4'>
          <div className='flex flex-col items-center'>
            <span className='mt-1 size-2.5 shrink-0 rounded-full bg-card-selected-background' />
            {index < events.length - 1 && <span className='mt-1 w-0.5 flex-1 bg-border' />}
          </div>
          <div className='min-w-0 flex-1'>
            <div className='text-sm font-semibold'>{event.title}</div>
            {event.detail && <div className='text-xs text-muted-foreground'>{event.detail}</div>}
            {event.at && (
              <div className='text-[11px] text-muted-foreground/80'>
                {noddiFormatDate(event.at, NoddiDateFormats.DATE_TIME)}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const MS_PER_DAY = 86_400_000;

function formatEtaText(target: string): string {
  const days = Math.round((new Date(target).getTime() - Date.now()) / MS_PER_DAY);
  if (days < -1) {
    const absDays = Math.abs(days);
    return t`Was due ${absDays} days ago`;
  }
  if (days === -1) {
    return t`Was due yesterday`;
  }
  if (days === 0) {
    return t`Arriving today`;
  }
  if (days === 1) {
    return t`Arriving tomorrow`;
  }
  return t`Arriving in ${days} days`;
}

function getActivityTitleForStatus(status: ReturnType<typeof getQuoteStatusValue>): string | undefined {
  if (!status) {
    return undefined;
  }
  switch (status) {
    case 'ACCEPTED':
      return t`Offer accepted, processing`;
    case 'INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL':
      return t`Tires ordered from supplier`;
    case 'INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL':
      return t`Tires ordered (partial)`;
    case 'INVENTORY_RECEIVED_IN_FULL':
      return t`Tires arrived`;
    case 'INVENTORY_RECEIVED_PARTIAL':
      return t`Tires arrived (partial)`;
    case 'TIRE_MOUNTING_BOOKED':
      return t`Mounting booked`;
    case 'TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY':
      return t`Tires mounted at facility`;
    case 'TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER':
      return t`Tires mounted at customer`;
    case 'FULFILLED':
      return t`Order completed`;
    case 'FULFILLED_PARTIALLY':
      return t`Order completed (partial)`;
    default:
      return undefined;
  }
}
